/**
 * PATH: packages/email-hub/src/feature/reader-header/command/useReaderHeaderShortcuts.ts
 * FILE_ID: EMAILHUB-READERHEADER-SHORTCUTS-001
 * PURPOSE: Own the header shortcut policy: Ctrl/Cmd+F find, D done, ? shortcut help.
 * ROLE IN ARCHITECTURE: Keyboard seam of the shared reader header (plan shortcut policy).
 * MAIN EXPORTS: useReaderHeaderShortcuts, READER_HEADER_SHORTCUTS.
 * NON-RESPONSIBILITIES: j/k navigation and @ ping stay in useReaderShortcuts (full view).
 * NOTES FOR FUTURE AI: Single-owner rule — only the most recently mounted reader
 *   handles keys, so a preview under a full-view overlay never double-fires.
 *   Never add an unmodified destructive shortcut here.
 */
import { useEffect, useRef, useState } from 'react'

/** Operator-facing rows for the `?` help overlay. */
export const READER_HEADER_SHORTCUTS: readonly {
  keys: string
  description: string
}[] = [
  { keys: 'Ctrl/Cmd + F', description: 'Find in this email' },
  { keys: 'D', description: 'Done — archive and complete linked work' },
  { keys: '?', description: 'Show keyboard shortcuts' },
  { keys: 'Esc', description: 'Close menu, dialog, or this overlay' },
]

/** Keys must never act while the operator is typing or a layer owns focus. */
function isBlockedTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  if (target.isContentEditable) return true
  return Boolean(target.closest('[role="dialog"], [role="menu"], [contenteditable="true"]'))
}

/** Most recently mounted reader owns the keys (single-owner policy). */
const ownerStack: symbol[] = []

export interface ReaderHeaderShortcutHandlers {
  onFind: () => void
  onDone?: () => void
  /** Disable entirely (e.g. flag-off host renders no shared header). */
  enabled?: boolean
}

/**
 * Bind the command shortcuts for one reader surface.
 *
 * Inputs: find/done handlers; `enabled` gate.
 * Output: `{ helpOpen, setHelpOpen }` for the ShortcutHelp overlay.
 * Side effects: one document keydown listener; registers itself on the
 * module-level owner stack for the lifetime of the mount.
 * Failure modes: none — non-owners and blocked targets simply ignore keys.
 */
export function useReaderHeaderShortcuts({
  onFind,
  onDone,
  enabled = true,
}: ReaderHeaderShortcutHandlers) {
  const [helpOpen, setHelpOpen] = useState(false)
  const handlers = useRef({ onFind, onDone })
  handlers.current = { onFind, onDone }

  useEffect(() => {
    if (!enabled) return
    const token = Symbol('reader-header-shortcut-owner')
    ownerStack.push(token)

    const onKeyDown = (event: KeyboardEvent) => {
      if (ownerStack[ownerStack.length - 1] !== token) return
      const key = event.key
      if ((event.metaKey || event.ctrlKey) && (key === 'f' || key === 'F')) {
        if (event.altKey || event.shiftKey) return
        event.preventDefault()
        handlers.current.onFind()
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isBlockedTarget(event.target)) return
      if (key === '?') {
        event.preventDefault()
        setHelpOpen((open) => !open)
        return
      }
      if ((key === 'd' || key === 'D') && handlers.current.onDone) {
        event.preventDefault()
        handlers.current.onDone()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      const index = ownerStack.indexOf(token)
      if (index >= 0) ownerStack.splice(index, 1)
    }
  }, [enabled])

  return { helpOpen, setHelpOpen }
}
